import React, {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {ActivationChannel} from '../../domain/entities/Activation.ts'
import {CRM_CHANNELS} from '../../domain/entities/Pipeline.ts'
import {
    CONVERSION_GROUPS,
    ConversionStage,
    PENDING_REASONS,
    PendingReason,
    PRINT_CHANNELS,
    PrintChannel,
} from '../../domain/entities/ConversionPipeline.ts'
import {dateInputToMillis, FIELD_CLASS, tomorrowDateInput} from './pipelineView.ts'
import {Field, FormDialog} from './PipelineDialogs.tsx'

const TEXTAREA = `${FIELD_CLASS} h-auto py-2 resize-none`

// Details pending (SET_STAGE with reason) and Change reason (SET_PENDING_REASON).
export const PendingReasonDialog: React.FC<{
    isOpen: boolean
    title: string
    submitLabel: string
    reasons?: PendingReason[]
    initial?: PendingReason | null
    onClose: () => void
    onSubmit: (reason: PendingReason, note: string) => Promise<unknown>
}> = ({isOpen, title, submitLabel, reasons, initial, onClose, onSubmit}) => {
    const {t} = useTranslation('dashboard')
    const [reason, setReason] = useState<PendingReason | ''>('')
    const [note, setNote] = useState('')
    useEffect(() => {
        if (!isOpen) return
        setReason(initial ?? '')
        setNote('')
    }, [isOpen, initial])
    const list = reasons?.length ? reasons : PENDING_REASONS

    return (
        <FormDialog isOpen={isOpen} title={title} submitLabel={submitLabel} canSubmit={!!reason && reason !== initial}
                    onClose={onClose} onSubmit={() => onSubmit(reason as PendingReason, note.trim())}>
            <fieldset className="flex flex-col gap-2">
                <legend className="text-[12.5px] font-medium text-neutralBlack-700 mb-1.5">{t('pipelines.pending.reason')}</legend>
                {list.map((r) => (
                    <label key={r} className="flex items-start gap-2.5 cursor-pointer">
                        <input type="radio" name="pending-reason" value={r} checked={reason === r} onChange={() => setReason(r)}
                               className="mt-[3px] accent-neutralBlack-900"/>
                        <span className="flex flex-col">
                            <span className="text-[13.5px] text-neutralBlack-900">{t(`pipelines.pendingReason.${r}`)}</span>
                            <span className="text-[12px] text-neutralBlack-600">{t(`pipelines.pendingReasonHelp.${r}`)}</span>
                        </span>
                    </label>
                ))}
            </fieldset>
            <Field label={t('pipelines.form.noteOptional')} htmlFor="pending-note">
                <textarea id="pending-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={TEXTAREA}/>
            </Field>
        </FormDialog>
    )
}

// Mark draft as sent: TOUCHPOINT_DONE without touchpointId in Draft ready or
// Changes · Adaptation ready (→ Waiting for customer OK), with the pages.
export const DraftSentDialog: React.FC<{
    isOpen: boolean
    defaultChannel: ActivationChannel
    defaultPages: number | null
    onClose: () => void
    onSubmit: (data: {channel: ActivationChannel; pages?: number; note: string}) => Promise<unknown>
}> = ({isOpen, defaultChannel, defaultPages, onClose, onSubmit}) => {
    const {t} = useTranslation('dashboard')
    const [channel, setChannel] = useState<ActivationChannel>(defaultChannel)
    const [pages, setPages] = useState('')
    const [note, setNote] = useState('')
    useEffect(() => {
        if (!isOpen) return
        setChannel(defaultChannel)
        setPages(defaultPages != null ? String(defaultPages) : '')
        setNote('')
    }, [isOpen, defaultChannel, defaultPages])
    const pagesNumber = pages.trim() ? Number(pages) : undefined
    const pagesValid = pagesNumber === undefined || (Number.isInteger(pagesNumber) && pagesNumber >= 1)

    return (
        <FormDialog isOpen={isOpen} title={t('pipelines.draftSent.title')} submitLabel={t('pipelines.draftSent.submit')}
                    canSubmit={pagesValid} onClose={onClose}
                    onSubmit={() => onSubmit({channel, pages: pagesNumber, note: note.trim()})}>
            <p className="text-[12.5px] leading-[1.5] text-neutralBlack-700">{t('pipelines.draftSent.hint')}</p>
            <Field label={t('pipelines.form.pages')} htmlFor="draft-pages" hint={t('pipelines.draftSent.pagesHint')}>
                <input id="draft-pages" type="number" min={1} step={1} inputMode="numeric" value={pages}
                       onChange={(e) => setPages(e.target.value)} className={FIELD_CLASS}/>
            </Field>
            <Field label={t('pipelines.form.channel')} htmlFor="draft-channel">
                <select id="draft-channel" value={channel} onChange={(e) => setChannel(e.target.value as ActivationChannel)} className={FIELD_CLASS}>
                    {CRM_CHANNELS.map((c) => <option key={c} value={c}>{t(`pipelines.channel.${c}`)}</option>)}
                </select>
            </Field>
            <Field label={t('pipelines.form.noteOptional')} htmlFor="draft-note">
                <textarea id="draft-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={TEXTAREA}/>
            </Field>
        </FormDialog>
    )
}

// Put on hold (HOLD) until a review date.
export const HoldDialog: React.FC<{
    isOpen: boolean
    onClose: () => void
    onSubmit: (data: {date: number; note: string}) => Promise<unknown>
}> = ({isOpen, onClose, onSubmit}) => {
    const {t} = useTranslation('dashboard')
    const [date, setDate] = useState('')
    const [note, setNote] = useState('')
    useEffect(() => {
        if (!isOpen) return
        setDate('')
        setNote('')
    }, [isOpen])
    const millis = dateInputToMillis(date)
    const valid = millis != null && millis > Date.now()

    return (
        <FormDialog isOpen={isOpen} title={t('pipelines.hold.title')} submitLabel={t('pipelines.hold.submit')}
                    canSubmit={valid} onClose={onClose} onSubmit={() => onSubmit({date: millis!, note: note.trim()})}>
            <p className="text-[12.5px] leading-[1.5] text-neutralBlack-700">{t('pipelines.hold.hint')}</p>
            <Field label={t('pipelines.hold.date')} htmlFor="hold-date">
                <input id="hold-date" type="date" value={date} min={tomorrowDateInput()} required
                       onChange={(e) => setDate(e.target.value)} className={FIELD_CLASS}/>
            </Field>
            <Field label={t('pipelines.form.noteOptional')} htmlFor="hold-note">
                <textarea id="hold-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={TEXTAREA}/>
            </Field>
        </FormDialog>
    )
}

// Override (OVERRIDE_STAGE): any of the 24 stages, with a mandatory note.
export const OverrideStageDialog: React.FC<{
    request: {currentStage: ConversionStage} | null
    onClose: () => void
    onSubmit: (stage: ConversionStage, note: string) => Promise<unknown>
}> = ({request, onClose, onSubmit}) => {
    const {t} = useTranslation('dashboard')
    const [stage, setStage] = useState<ConversionStage | ''>('')
    const [note, setNote] = useState('')
    useEffect(() => {
        if (!request) return
        setStage('')
        setNote('')
    }, [request])

    return (
        <FormDialog isOpen={!!request} title={t('pipelines.override.title')} submitLabel={t('pipelines.override.submit')}
                    canSubmit={!!stage && !!note.trim()} onClose={onClose}
                    onSubmit={() => onSubmit(stage as ConversionStage, note.trim())}>
            <p className="text-[12.5px] leading-[1.5] text-neutralBlack-700">{t('pipelines.override.hint')}</p>
            <Field label={t('pipelines.override.stage')} htmlFor="override-stage">
                <select id="override-stage" value={stage} onChange={(e) => setStage(e.target.value as ConversionStage)} className={FIELD_CLASS} required>
                    <option value="">{t('pipelines.override.choose')}</option>
                    {CONVERSION_GROUPS.map((g) => (
                        <optgroup key={g.id} label={t(`pipelines.group.${g.id}`)}>
                            {g.stages.map((s) => (
                                <option key={s} value={s} disabled={s === request?.currentStage}>{t(`pipelines.stage.${s}`)}</option>
                            ))}
                        </optgroup>
                    ))}
                </select>
            </Field>
            <Field label={t('pipelines.override.note')} htmlFor="override-note" hint={t('pipelines.override.noteHint')}>
                <textarea id="override-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} className={TEXTAREA} required aria-required="true"/>
            </Field>
        </FormDialog>
    )
}

// "Move to…": the keyboard alternative to drag and drop, limited to the
// columns that take this card. The pick is handed over once the dialog has
// closed, so the follow-up form (if any) opens on its own.
export const MoveToDialog: React.FC<{
    request: {targets: ConversionStage[]} | null
    onClose: (picked: ConversionStage | null) => void
}> = ({request, onClose}) => {
    const {t} = useTranslation('dashboard')
    const [stage, setStage] = useState<ConversionStage | ''>('')
    const picked = useRef<ConversionStage | null>(null)
    useEffect(() => {
        if (!request) return
        setStage('')
        picked.current = null
    }, [request])

    return (
        <FormDialog isOpen={!!request} title={t('pipelines.moveTo.title')} submitLabel={t('pipelines.moveTo.submit')}
                    canSubmit={!!stage} onClose={() => onClose(picked.current)}
                    onSubmit={() => { picked.current = stage || null; return Promise.resolve() }}>
            {request && request.targets.length === 0 ? (
                <p className="text-[13px] text-neutralBlack-700">{t('pipelines.moveTo.none')}</p>
            ) : (
                <fieldset className="flex flex-col gap-2">
                    <legend className="text-[12.5px] font-medium text-neutralBlack-700 mb-1.5">{t('pipelines.moveTo.stage')}</legend>
                    {request?.targets.map((s) => (
                        <label key={s} className="flex items-center gap-2.5 text-[13.5px] text-neutralBlack-900 cursor-pointer">
                            <input type="radio" name="move-to" value={s} checked={stage === s} onChange={() => setStage(s)}
                                   className="accent-neutralBlack-900"/>
                            {t(`pipelines.stage.${s}`)}
                        </label>
                    ))}
                </fieldset>
            )}
        </FormDialog>
    )
}

// One value: flipbook URL (SET_FLIPBOOK), pages (SET_PAGES) or tracking URL
// (MARK_SHIPPED, optional).
export const ValueDialog: React.FC<{
    request: {kind: 'flipbook' | 'pages' | 'shipped'; initial?: string | null} | null
    onClose: () => void
    onSubmit: (value: string) => Promise<unknown>
}> = ({request, onClose, onSubmit}) => {
    const {t} = useTranslation('dashboard')
    const [value, setValue] = useState('')
    useEffect(() => { if (request) setValue(request.initial ?? '') }, [request])
    const kind = request?.kind ?? 'pages'
    const trimmed = value.trim()
    const isHttps = (v: string) => /^https:\/\/\S+$/i.test(v)
    const valid = kind === 'pages'
        ? Number.isInteger(Number(trimmed)) && Number(trimmed) >= 1
        : kind === 'flipbook' ? isHttps(trimmed) : (!trimmed || isHttps(trimmed))

    return (
        <FormDialog isOpen={!!request} title={t(`pipelines.value.${kind}.title`)} submitLabel={t(`pipelines.value.${kind}.submit`)}
                    canSubmit={valid} onClose={onClose} onSubmit={() => onSubmit(trimmed)}>
            <Field label={t(`pipelines.value.${kind}.label`)} htmlFor="value-input" hint={t(`pipelines.value.${kind}.hint`)}>
                <input id="value-input" type={kind === 'pages' ? 'number' : 'url'} min={kind === 'pages' ? 1 : undefined}
                       inputMode={kind === 'pages' ? 'numeric' : 'url'} value={value} onChange={(e) => setValue(e.target.value)}
                       placeholder={kind === 'pages' ? '' : 'https://'} className={FIELD_CLASS} autoFocus/>
            </Field>
        </FormDialog>
    )
}

// Sent to print shop (MARK_SENT_TO_PRINT) with an optional print channel.
export const SentToPrintDialog: React.FC<{
    isOpen: boolean
    initial: PrintChannel | null
    onClose: () => void
    onSubmit: (printChannel: PrintChannel | undefined) => Promise<unknown>
}> = ({isOpen, initial, onClose, onSubmit}) => {
    const {t} = useTranslation('dashboard')
    const [channel, setChannel] = useState<PrintChannel | ''>('')
    useEffect(() => { if (isOpen) setChannel(initial ?? '') }, [isOpen, initial])

    return (
        <FormDialog isOpen={isOpen} title={t('pipelines.print.title')} submitLabel={t('pipelines.print.submit')}
                    canSubmit onClose={onClose} onSubmit={() => onSubmit(channel || undefined)}>
            <Field label={t('pipelines.print.channel')} htmlFor="print-channel">
                <select id="print-channel" value={channel} onChange={(e) => setChannel(e.target.value as PrintChannel | '')} className={FIELD_CLASS}>
                    <option value="">{t('pipelines.print.channelDefault')}</option>
                    {PRINT_CHANNELS.map((c) => <option key={c} value={c}>{t(`pipelines.printChannel.${c}`, {defaultValue: c})}</option>)}
                </select>
            </Field>
        </FormDialog>
    )
}
